/**
 * ============================================================================
 * KbBlockInsertPopover — Popover "Insert block…" acionado pelo botão "+" do
 *                        handle lateral (estilo GitBook). Insere o bloco
 *                        selecionado APÓS a posição alvo `insertAt`.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.1.0 - Preserva scroll externo e bloqueia autofocus
 *            de fechamento do Radix ao inserir blocos pelo botão +.
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial (Bloco Handle GitBook).
 * ============================================================================
 */
import type { Editor } from "@tiptap/react";
import { useMemo, useState } from "react";
import { PopoverContent } from "@/components/ui/popover";
import { BLOCK_CATALOG, BLOCK_GROUP_LABELS, type BlockGroup } from "./kbBlockCatalog";

interface Props {
  editor: Editor;
  /** Posição do documento onde inserir o bloco (geralmente `after(bloco)`). */
  insertAt: number;
  /** Fecha o popover após inserir. */
  onClose: () => void;
}

export function KbBlockInsertPopoverContent({ editor, insertAt, onClose }: Props) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return BLOCK_CATALOG;
    return BLOCK_CATALOG.filter(
      (it) =>
        it.title.toLowerCase().includes(q) ||
        it.description.toLowerCase().includes(q) ||
        it.keywords.some((k) => k.includes(q)),
    );
  }, [query]);

  const groups: BlockGroup[] = ["basic", "advanced"];

  const runCommand = (item: (typeof BLOCK_CATALOG)[number]) => {
    // Mantém a posição visual da coluna de edição. Alguns comandos Tiptap
    // chamam focus() internamente e o navegador pode tentar rolar até a seleção.
    preserveEditorScroll(editor, () => {
      // Posiciona o cursor no ponto de inserção antes de rodar o comando.
      // Isso permite que setNode/toggleBulletList/insertContent atuem no lugar certo.
      editor.chain().focus().setTextSelection(insertAt).run();
      item.command({ editor });
      onClose();
    });
  };

  return (
    <PopoverContent
      align="start"
      side="bottom"
      sideOffset={4}
      data-kb-editor-ui="true"
      className="p-0 w-72"
      onOpenAutoFocus={(e) => {
        // Deixa o input de busca receber foco (o autoFocus abaixo cuida disso)
        e.preventDefault();
      }}
      onCloseAutoFocus={(e) => {
        // Radix tenta devolver foco ao botão +; em ProseMirror isso pode causar
        // salto de scroll. O editor preserva a posição visual manualmente.
        e.preventDefault();
      }}
    >
      <div className="p-2 border-b">
        <input
          autoFocus
          placeholder="Inserir bloco…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full text-sm bg-transparent outline-none placeholder:text-muted-foreground"
        />
      </div>
      <div className="max-h-80 overflow-auto py-1">
        {groups.map((g) => {
          const items = filtered.filter((it) => it.group === g);
          if (items.length === 0) return null;
          return (
            <div key={g} className="mb-1">
              <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {BLOCK_GROUP_LABELS[g]}
              </div>
              {items.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => runCommand(item)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent text-left"
                  >
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate">{item.title}</span>
                  </button>
                );
              })}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="px-3 py-4 text-xs text-muted-foreground text-center">
            Nenhum bloco encontrado
          </div>
        )}
      </div>
    </PopoverContent>
  );
}

/** Captura e restaura a rolagem externa do editor KB após ações de bloco. */
function preserveEditorScroll(editor: Editor, action: () => void) {
  const scrollEl = editor.view.dom.closest("[data-kb-scroll]") as HTMLElement | null;
  const top = scrollEl?.scrollTop ?? 0;
  const left = scrollEl?.scrollLeft ?? 0;

  action();

  // Restaura em mais de um frame para cobrir foco Radix + transação ProseMirror.
  [0, 1, 2].forEach((frame) => {
    window.requestAnimationFrame(() => {
      if (frame > 0) window.requestAnimationFrame(() => restoreScroll(scrollEl, top, left));
      else restoreScroll(scrollEl, top, left);
    });
  });
}

function restoreScroll(scrollEl: HTMLElement | null, top: number, left: number) {
  if (!scrollEl) return;
  scrollEl.scrollTop = top;
  scrollEl.scrollLeft = left;
}
